import React, { useState, useEffect, useCallback } from 'react';
import { ExternalLink, Plus, Edit, Copy, Trash2, Loader2, Search, ToggleLeft, ToggleRight, Clock, Eye, MousePointer, ArrowRight, ChevronDown, ChevronUp, X, BarChart3, TrendingUp, Users, Target, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../config/apiBase';


const EMPTY_FORM = {
    name: '', slug: '', status: 'inactive', description: '',
    destination_url: '', fallback_url: '', destination_type: 'externo', auto_redirect: true,
    countdown_seconds: 5, preserve_query_params: true,
    main_badge: '', main_title: '', main_message: '',
    ad_type: 'none', ad_title: '', ad_text: '', banner_title: '', banner_tag: '',
    primary_button_enabled: true, primary_button_text: 'Continuar',
    secondary_button_enabled: false, secondary_button_text: 'Cancelar',
    secondary_button_action: 'cancel', secondary_button_url: '',
    accent_color: 'var(--sv-accent)', primary_button_color: 'var(--sv-accent)', show_logo: true,
    starts_at: '', ends_at: '',
};

const AD_TYPES = [
    { value: 'none', label: 'Nenhum' },
    { value: 'text', label: 'Texto simples' },
    { value: 'campaign', label: 'Campanha personalizada' },
    { value: 'ads_warning', label: 'Aviso Google Ads' },
];

const DEST_TYPES = [
    { value: 'compra', label: 'Compra' },
    { value: 'grupo_supervitrine', label: 'Grupo SuperVitrine' },
    { value: 'landing_page', label: 'Landing Page' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'telegram', label: 'Telegram' },
    { value: 'externo', label: 'Externo' },
    { value: 'outro', label: 'Outro' },
];

const SEC_ACTIONS = [
    { value: 'cancel', label: 'Cancelar redirecionamento' },
    { value: 'url', label: 'Ir para URL customizada' },
    { value: 'pause', label: 'Pausar contador' },
];

export default function RedirectCampaignsPage() {
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [saving, setSaving] = useState(false);
    const [section, setSection] = useState('general');
    const [view, setView] = useState('list'); // 'list' | 'metrics'
    const [metrics, setMetrics] = useState(null);
    const [metricsLoading, setMetricsLoading] = useState(false);
    const [metricsDays, setMetricsDays] = useState(30);
    const [metricsSlug, setMetricsSlug] = useState('');

    const fetchCampaigns = useCallback(async () => {
        try {
            const r = await fetch(`${API}/redirect-campaigns`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (r.ok) setCampaigns(await r.json());
        } catch {} finally { setLoading(false); }
    }, [token]);

    useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

    const fetchMetrics = useCallback(async (days = metricsDays, slug = metricsSlug) => {
        setMetricsLoading(true);
        try {
            let url = `${API}/redirect-campaigns/metrics/dashboard?days=${days}`;
            if (slug) url += `&slug=${slug}`;
            const r = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
            if (r.ok) setMetrics(await r.json());
        } catch {} finally { setMetricsLoading(false); }
    }, [token, metricsDays, metricsSlug]);

    const openNew = () => { setForm({ ...EMPTY_FORM }); setEditing('new'); setSection('general'); };
    const openEdit = (c) => {
        setForm({ ...EMPTY_FORM, ...c, starts_at: c.starts_at || '', ends_at: c.ends_at || '' });
        setEditing(c.campaign_id);
        setSection('general');
    };
    const closeForm = () => { setEditing(null); };

    const handleSave = async () => {
        if (!form.name || !form.slug) { toast.error('Nome e slug obrigatorios'); return; }
        if (!form.destination_url) { toast.error('URL de destino obrigatoria'); return; }
        setSaving(true);
        try {
            const isNew = editing === 'new';
            const url = isNew ? `${API}/redirect-campaigns` : `${API}/redirect-campaigns/${editing}`;
            const method = isNew ? 'POST' : 'PUT';
            const r = await fetch(url, { method, headers, body: JSON.stringify(form) });
            const d = await r.json();
            if (r.ok) { toast.success(isNew ? 'Campanha criada' : 'Campanha atualizada'); closeForm(); fetchCampaigns(); }
            else toast.error(d.detail || 'Erro');
        } catch { toast.error('Erro de conexao'); } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Excluir campanha?')) return;
        try {
            const r = await fetch(`${API}/redirect-campaigns/${id}`, { method: 'DELETE', headers });
            if (r.ok) { toast.success('Excluida'); fetchCampaigns(); } else toast.error('Erro');
        } catch { toast.error('Erro'); }
    };

    const handleDuplicate = async (id) => {
        try {
            const r = await fetch(`${API}/redirect-campaigns/${id}/duplicate`, { method: 'POST', headers });
            if (r.ok) { toast.success('Duplicada'); fetchCampaigns(); } else toast.error('Erro');
        } catch { toast.error('Erro'); }
    };

    const handleToggle = async (c) => {
        const newStatus = c.status === 'active' ? 'inactive' : 'active';
        try {
            const r = await fetch(`${API}/redirect-campaigns/${c.campaign_id}`, { method: 'PUT', headers, body: JSON.stringify({ status: newStatus }) });
            if (r.ok) { toast.success(newStatus === 'active' ? 'Ativada' : 'Desativada'); fetchCampaigns(); }
        } catch { toast.error('Erro'); }
    };

    const copyLink = (slug) => {
        navigator.clipboard.writeText(`https://ir.supervitrineofertas.com/r/${slug}`);
        toast.success('Link copiado!');
    };

    const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
    const filtered = campaigns.filter(c => !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.slug?.includes(search.toLowerCase()));

    const SECTIONS = [
        { key: 'general', label: 'Dados gerais' },
        { key: 'destination', label: 'Destino' },
        { key: 'countdown', label: 'Contagem' },
        { key: 'content', label: 'Conteudo' },
        { key: 'ad', label: 'Anuncio' },
        { key: 'buttons', label: 'Botoes' },
        { key: 'appearance', label: 'Aparencia' },
        { key: 'schedule', label: 'Agendamento' },
    ];

    const InputField = ({ label, value, onChange, placeholder, type = 'text', ...rest }) => (
        <div className="mb-3">
            <label className="block text-[10px] font-semibold mb-1 uppercase tracking-wider" style={{ color: 'var(--muted)' }}>{label}</label>
            <input type={type} value={value || ''} onChange={e => onChange(type === 'number' ? Number(e.target.value) : e.target.value)}
                placeholder={placeholder} className="w-full px-3 py-2 rounded-lg text-xs outline-none" style={{ background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }} {...rest} />
        </div>
    );

    const Toggle = ({ label, value, onChange }) => (
        <div className="flex items-center justify-between mb-3 py-1">
            <span className="text-xs" style={{ color: 'var(--text)' }}>{label}</span>
            <button onClick={() => onChange(!value)} className="p-0.5" data-testid={`toggle-${label}`}>
                {value ? <ToggleRight size={22} style={{ color: 'var(--sv-accent)' }} /> : <ToggleLeft size={22} style={{ color: 'var(--muted)' }} />}
            </button>
        </div>
    );

    const SelectField = ({ label, value, onChange, options }) => (
        <div className="mb-3">
            <label className="block text-[10px] font-semibold mb-1 uppercase tracking-wider" style={{ color: 'var(--muted)' }}>{label}</label>
            <select value={value || ''} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2 rounded-lg text-xs outline-none" style={{ background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }}>
                {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </div>
    );

    // === FORM VIEW ===
    if (editing) return (
        <div className="max-w-4xl mx-auto space-y-4" data-testid="rc-form">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>{editing === 'new' ? 'Nova Campanha' : 'Editar Campanha'}</h2>
                <button onClick={closeForm} className="p-2 rounded-lg hover:bg-white/5"><X size={16} style={{ color: 'var(--muted)' }} /></button>
            </div>

            {/* Section tabs */}
            <div className="flex flex-wrap gap-1.5">
                {SECTIONS.map(s => (
                    <button key={s.key} onClick={() => setSection(s.key)}
                        className="px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all"
                        style={{ background: section === s.key ? 'var(--sv-secondary)' : 'var(--surface)', color: section === s.key ? 'var(--sv-on-secondary)' : 'var(--muted)', border: '1px solid var(--border)' }}
                        data-testid={`rc-tab-${s.key}`}>{s.label}</button>
                ))}
            </div>

            <div className="rounded-xl p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                {section === 'general' && <>
                    <InputField label="Nome da campanha" value={form.name} onChange={v => set('name', v)} placeholder="Ex: Black Friday 2026" />
                    <InputField label="Slug (URL)" value={form.slug} onChange={v => set('slug', v.toLowerCase().replace(/[^a-z0-9-]/g, ''))} placeholder="ex: black-friday-2026" />
                    <p className="text-[9px] -mt-2 mb-3" style={{ color: 'var(--muted)' }}>Link: https://ir.supervitrineofertas.com/r/{form.slug || '...'}</p>
                    <InputField label="Descricao interna" value={form.description} onChange={v => set('description', v)} placeholder="Notas internas..." />
                    <SelectField label="Status" value={form.status} onChange={v => set('status', v)} options={[{ value: 'inactive', label: 'Inativo' }, { value: 'active', label: 'Ativo' }]} />
                </>}
                {section === 'destination' && <>
                    <InputField label="URL de destino" value={form.destination_url} onChange={v => set('destination_url', v)} placeholder="https://..." />
                    <InputField label="URL fallback (opcional)" value={form.fallback_url} onChange={v => set('fallback_url', v)} placeholder="https://..." />
                    <SelectField label="Tipo de destino" value={form.destination_type} onChange={v => set('destination_type', v)} options={DEST_TYPES} />
                    <Toggle label="Preservar query params/UTMs" value={form.preserve_query_params} onChange={v => set('preserve_query_params', v)} />
                </>}
                {section === 'countdown' && <>
                    <InputField label="Segundos de espera" value={form.countdown_seconds} onChange={v => set('countdown_seconds', v)} type="number" min={0} max={60} />
                    <Toggle label="Redirecionamento automatico" value={form.auto_redirect} onChange={v => set('auto_redirect', v)} />
                </>}
                {section === 'content' && <>
                    <InputField label="Badge superior" value={form.main_badge} onChange={v => set('main_badge', v)} placeholder="Ex: OFERTA ESPECIAL" />
                    <InputField label="Titulo principal" value={form.main_title} onChange={v => set('main_title', v)} placeholder="Voce esta sendo redirecionado..." />
                    <InputField label="Mensagem" value={form.main_message} onChange={v => set('main_message', v)} placeholder="Aguarde enquanto preparamos sua oferta." />
                </>}
                {section === 'ad' && <>
                    <SelectField label="Tipo de anuncio" value={form.ad_type} onChange={v => set('ad_type', v)} options={AD_TYPES} />
                    {form.ad_type !== 'none' && <>
                        <InputField label="Titulo do anuncio" value={form.ad_title} onChange={v => set('ad_title', v)} placeholder="Titulo..." />
                        <InputField label="Texto do anuncio" value={form.ad_text} onChange={v => set('ad_text', v)} placeholder="Texto..." />
                        <InputField label="Titulo do banner" value={form.banner_title} onChange={v => set('banner_title', v)} placeholder="Banner..." />
                        <InputField label="Tag do banner" value={form.banner_tag} onChange={v => set('banner_tag', v)} placeholder="Tag..." />
                    </>}
                </>}
                {section === 'buttons' && <>
                    <Toggle label="Botao principal" value={form.primary_button_enabled} onChange={v => set('primary_button_enabled', v)} />
                    {form.primary_button_enabled && <InputField label="Texto do botao principal" value={form.primary_button_text} onChange={v => set('primary_button_text', v)} />}
                    <Toggle label="Botao secundario" value={form.secondary_button_enabled} onChange={v => set('secondary_button_enabled', v)} />
                    {form.secondary_button_enabled && <>
                        <InputField label="Texto do botao secundario" value={form.secondary_button_text} onChange={v => set('secondary_button_text', v)} />
                        <SelectField label="Acao do botao secundario" value={form.secondary_button_action} onChange={v => set('secondary_button_action', v)} options={SEC_ACTIONS} />
                        {form.secondary_button_action === 'url' && <InputField label="URL do botao secundario" value={form.secondary_button_url} onChange={v => set('secondary_button_url', v)} placeholder="https://..." />}
                    </>}
                </>}
                {section === 'appearance' && <>
                    <div className="mb-3">
                        <label className="block text-[10px] font-semibold mb-1 uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Cor destaque</label>
                        <div className="flex items-center gap-2">
                            <input type="color" value={form.accent_color} onChange={e => set('accent_color', e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0" />
                            <span className="text-xs" style={{ color: 'var(--text)' }}>{form.accent_color}</span>
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="block text-[10px] font-semibold mb-1 uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Cor botao principal</label>
                        <div className="flex items-center gap-2">
                            <input type="color" value={form.primary_button_color} onChange={e => set('primary_button_color', e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0" />
                            <span className="text-xs" style={{ color: 'var(--text)' }}>{form.primary_button_color}</span>
                        </div>
                    </div>
                    <Toggle label="Exibir logo" value={form.show_logo} onChange={v => set('show_logo', v)} />
                </>}
                {section === 'schedule' && <>
                    <InputField label="Inicio (opcional)" value={form.starts_at} onChange={v => set('starts_at', v)} type="datetime-local" />
                    <InputField label="Termino (opcional)" value={form.ends_at} onChange={v => set('ends_at', v)} type="datetime-local" />
                </>}
            </div>

            <div className="flex gap-2">
                <button onClick={handleSave} disabled={saving}
                    className="flex-1 py-2.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-all hover:scale-[1.01]"
                    style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="rc-save-btn">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : null} Salvar
                </button>
                <button onClick={closeForm} className="px-4 py-2.5 rounded-lg text-xs font-semibold" style={{ background: 'var(--surface)', color: 'var(--muted)', border: '1px solid var(--border)' }}>Cancelar</button>
            </div>
        </div>
    );

    // === METRICS VIEW ===
    if (view === 'metrics') {
        const f = metrics?.funnel || {};
        const MetricCard = ({ label, value, sub, color }) => (
            <div className="rounded-xl p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--muted)' }}>{label}</p>
                <p className="text-xl font-bold" style={{ color: color || 'var(--text)', fontFamily: "var(--font-display)" }}>{value ?? '—'}</p>
                {sub && <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted)' }}>{sub}</p>}
            </div>
        );
        return (
            <div className="max-w-5xl mx-auto space-y-4" data-testid="rc-metrics">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Metricas do Redirecionador</h2>
                    <button onClick={() => setView('list')} className="px-3 py-1.5 rounded-lg text-xs font-semibold" style={{ background: 'var(--surface)', color: 'var(--muted)', border: '1px solid var(--border)' }}>
                        Voltar
                    </button>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-2">
                    {[7, 14, 30, 90].map(d => (
                        <button key={d} onClick={() => { setMetricsDays(d); fetchMetrics(d, metricsSlug); }}
                            className="px-3 py-1.5 rounded-lg text-[10px] font-semibold"
                            style={{ background: metricsDays === d ? 'var(--sv-secondary)' : 'var(--surface)', color: metricsDays === d ? 'var(--sv-on-secondary)' : 'var(--muted)', border: '1px solid var(--border)' }}
                            data-testid={`rc-filter-${d}d`}>{d}d</button>
                    ))}
                    <select value={metricsSlug} onChange={e => { setMetricsSlug(e.target.value); fetchMetrics(metricsDays, e.target.value); }}
                        className="px-3 py-1.5 rounded-lg text-[10px] outline-none" style={{ background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)' }} data-testid="rc-filter-slug">
                        <option value="">Todas campanhas</option>
                        {campaigns.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                    </select>
                </div>

                {metricsLoading ? <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin" style={{ color: 'var(--sv-accent)' }} /></div> : metrics ? <>
                    {/* KPI Cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <MetricCard label="Views" value={f.views} color="#3B82F6" />
                        <MetricCard label="Views unicas" value={f.unique_views} color="#8B5CF6" />
                        <MetricCard label="Cliques primario" value={f.primary_clicks} sub={`CTR ${f.ctr_primary || 0}%`} color="#7DB3FF" />
                        <MetricCard label="Redirects" value={f.completed_redirects} sub={`Conv ${f.conversion_rate || 0}%`} color="#22c55e" />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <MetricCard label="Cliques secundario" value={f.secondary_clicks} color="#F59E0B" />
                        <MetricCard label="Cancelamentos" value={f.cancellations} color="#EF4444" />
                        <MetricCard label="Abandonos" value={f.abandons} color="#6B7280" />
                        <MetricCard label="Tempo medio" value={`${metrics.avg_time_to_action || 0}s`} sub="ate acao" color="var(--text)" />
                    </div>

                    {/* Funnel */}
                    <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <p className="text-xs font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Funil de Conversao</p>
                        {[
                            { label: 'Views', val: f.views, color: '#3B82F6' },
                            { label: 'Views unicas', val: f.unique_views, color: '#8B5CF6' },
                            { label: 'Cliques primario', val: f.primary_clicks, color: 'var(--sv-accent)' },
                            { label: 'Redirects concluidos', val: f.completed_redirects, color: '#22c55e' },
                        ].map((step, i) => {
                            const maxVal = f.views || 1;
                            const pct = Math.round((step.val / maxVal) * 100);
                            return (
                                <div key={i} className="mb-2">
                                    <div className="flex justify-between text-[10px] mb-0.5">
                                        <span style={{ color: 'var(--text)' }}>{step.label}</span>
                                        <span style={{ color: 'var(--muted)' }}>{step.val} ({pct}%)</span>
                                    </div>
                                    <div style={{ height: 6, background: '#1F1F1F', borderRadius: 3, overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${pct}%`, background: step.color, borderRadius: 3, transition: 'width 0.5s' }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Top campaigns & sources side by side */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                            <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Top Campanhas</p>
                            {metrics.top_campaigns?.length === 0 && <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Sem dados</p>}
                            {metrics.top_campaigns?.map((c, i) => (
                                <div key={i} className="flex justify-between py-1 text-[10px]" style={{ borderBottom: '1px solid var(--border)' }}>
                                    <span style={{ color: 'var(--text)' }}>/{c.slug}</span>
                                    <span style={{ color: 'var(--muted)' }}>{c.views}v | {c.clicks}c | {c.redirects}r</span>
                                </div>
                            ))}
                        </div>
                        <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                            <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Top Origens (UTM Source)</p>
                            {metrics.top_sources?.length === 0 && <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Sem dados</p>}
                            {metrics.top_sources?.map((s, i) => (
                                <div key={i} className="flex justify-between py-1 text-[10px]" style={{ borderBottom: '1px solid var(--border)' }}>
                                    <span style={{ color: 'var(--text)' }}>{s.source}</span>
                                    <span style={{ color: 'var(--muted)' }}>{s.count} eventos</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Devices */}
                    <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Dispositivos</p>
                        <div className="flex gap-3">
                            {metrics.devices?.map((d, i) => (
                                <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                                    <span className="text-[10px] font-semibold" style={{ color: 'var(--text)' }}>{d.device}</span>
                                    <span className="text-[10px]" style={{ color: 'var(--muted)' }}>{d.count}</span>
                                </div>
                            ))}
                            {(!metrics.devices || metrics.devices.length === 0) && <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Sem dados</p>}
                        </div>
                    </div>
                </> : <p className="text-center text-xs py-8" style={{ color: 'var(--muted)' }}>Sem dados de metricas.</p>}
            </div>
        );
    }

    // === LIST VIEW ===
    return (
        <div className="max-w-5xl mx-auto space-y-4" data-testid="rc-list">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Campanhas de Redirecionamento</h2>
                <div className="flex gap-2">
                    <button onClick={() => { setView('metrics'); fetchMetrics(); }} className="px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5"
                        style={{ background: 'var(--surface)', color: 'var(--muted)', border: '1px solid var(--border)' }} data-testid="rc-metrics-btn">
                        <BarChart3 size={14} /> Metricas
                    </button>
                    <button onClick={openNew} className="px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all hover:scale-[1.02]"
                        style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="rc-new-btn">
                        <Plus size={14} /> Nova Campanha
                    </button>
                </div>
            </div>

            <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted)' }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar campanhas..."
                    className="w-full pl-9 pr-4 py-2.5 rounded-lg text-xs outline-none" style={{ background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)' }}
                    data-testid="rc-search" />
            </div>

            {loading ? <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin" style={{ color: 'var(--sv-accent)' }} /></div> : (
                <div className="space-y-2">
                    {filtered.length === 0 ? (
                        <div className="text-center py-12 rounded-xl" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                            <p className="text-sm" style={{ color: 'var(--muted)' }}>Nenhuma campanha encontrada.</p>
                        </div>
                    ) : filtered.map(c => (
                        <div key={c.campaign_id} className="rounded-xl p-4 transition-all hover:scale-[1.002]" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`rc-item-${c.slug}`}>
                            <div className="flex items-start gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-xs font-bold truncate" style={{ color: 'var(--text)' }}>{c.name}</span>
                                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold shrink-0"
                                            style={{ background: c.status === 'active' ? 'rgba(34,197,94,0.12)' : 'rgba(107,114,128,0.12)', color: c.status === 'active' ? '#22c55e' : '#6B7280' }}>
                                            {c.status === 'active' ? 'Ativo' : 'Inativo'}
                                        </span>
                                    </div>
                                    <p className="text-[10px] mb-1" style={{ color: 'var(--muted)' }}>
                                        /{c.slug} &nbsp;→&nbsp; {c.destination_url?.substring(0, 50)}{c.destination_url?.length > 50 ? '...' : ''}
                                        {c.destination_type && c.destination_type !== 'externo' && <span> &nbsp;| {DEST_TYPES.find(d => d.value === c.destination_type)?.label || c.destination_type}</span>}
                                        {c.countdown_seconds > 0 && <span> &nbsp;| {c.countdown_seconds}s</span>}
                                        {c.ad_type !== 'none' && <span> &nbsp;| {AD_TYPES.find(a => a.value === c.ad_type)?.label}</span>}
                                    </p>
                                    <div className="flex items-center gap-3 text-[9px]" style={{ color: 'var(--muted)' }}>
                                        <span className="flex items-center gap-1"><Eye size={10} /> {c.metrics?.views || 0}</span>
                                        <span className="flex items-center gap-1"><Users size={10} /> {c.metrics?.unique_views || 0}u</span>
                                        <span className="flex items-center gap-1"><MousePointer size={10} /> {c.metrics?.primary_clicks || 0}</span>
                                        <span className="flex items-center gap-1"><ArrowRight size={10} /> {c.metrics?.completed_redirects || 0}</span>
                                        {(c.metrics?.views || 0) > 0 && <span className="font-semibold" style={{ color: 'var(--sv-accent)' }}>CTR {Math.round(((c.metrics?.primary_clicks || 0) / c.metrics.views) * 100)}%</span>}
                                        {c.starts_at && <span className="flex items-center gap-1"><Clock size={10} /> {new Date(c.starts_at).toLocaleDateString('pt-BR')}</span>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button onClick={() => handleToggle(c)} title={c.status === 'active' ? 'Desativar' : 'Ativar'} className="p-1.5 rounded-lg hover:bg-white/5" data-testid={`rc-toggle-${c.slug}`}>
                                        {c.status === 'active' ? <ToggleRight size={16} style={{ color: '#22c55e' }} /> : <ToggleLeft size={16} style={{ color: 'var(--muted)' }} />}
                                    </button>
                                    <button onClick={() => copyLink(c.slug)} title="Copiar link" className="p-1.5 rounded-lg hover:bg-white/5" data-testid={`rc-copy-${c.slug}`}>
                                        <Copy size={14} style={{ color: 'var(--muted)' }} />
                                    </button>
                                    <button onClick={() => openEdit(c)} title="Editar" className="p-1.5 rounded-lg hover:bg-white/5" data-testid={`rc-edit-${c.slug}`}>
                                        <Edit size={14} style={{ color: 'var(--muted)' }} />
                                    </button>
                                    <button onClick={() => handleDuplicate(c.campaign_id)} title="Duplicar" className="p-1.5 rounded-lg hover:bg-white/5" data-testid={`rc-dup-${c.slug}`}>
                                        <ExternalLink size={14} style={{ color: 'var(--muted)' }} />
                                    </button>
                                    <button onClick={() => handleDelete(c.campaign_id)} title="Excluir" className="p-1.5 rounded-lg hover:bg-white/5" data-testid={`rc-del-${c.slug}`}>
                                        <Trash2 size={14} style={{ color: '#EF4444' }} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
